import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface PortfolioItem {
  id: string;
  storagePath: string;
  createdAt: number;
}

export interface PortfolioItemWithUrl extends PortfolioItem {
  imageUrl: string;
}

const BUCKET = 'portfolio-images';
const EDGE_FN_URL = 'https://ybpbuzwfgeqrxkgdawhp.supabase.co/functions/v1/portfolio-manage';

function getPublicUrl(storagePath: string): string {
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);
  return data.publicUrl;
}

export function usePortfolioStore() {
  const [items, setItems] = useState<PortfolioItemWithUrl[]>([]);
  const [loading, setLoading] = useState(true);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('portfolio_photos')
        .select('id, storage_path, created_at')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Portfolio load error:', error);
        return;
      }

      const withUrls: PortfolioItemWithUrl[] = (data ?? []).map((row) => ({
        id: row.id as string,
        storagePath: row.storage_path as string,
        createdAt: new Date(row.created_at as string).getTime(),
        imageUrl: getPublicUrl(row.storage_path as string),
      }));

      setItems(withUrls);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Edge Function을 통해 업로드 (서비스 롤 키로 RLS 우회)
  const addItems = useCallback(
    async (files: File[]) => {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));

      const res = await fetch(EDGE_FN_URL, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const err = await res.text();
        console.error('Upload failed:', err);
        throw new Error('업로드에 실패했습니다');
      }

      await loadItems();
    },
    [loadItems]
  );

  // Edge Function을 통해 삭제
  const deleteItem = useCallback(
    async (id: string) => {
      const item = items.find((it) => it.id === id);
      if (!item) return;

      const res = await fetch(EDGE_FN_URL, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.id, storagePath: item.storagePath }),
      });

      if (!res.ok) {
        console.error('Delete failed:', await res.text());
      }

      await loadItems();
    },
    [items, loadItems]
  );

  return { items, loading, addItems, deleteItem };
}
